// Shared Google Maps / Places helper.
//
// Why this exists: the Add Customer and Create Project forms both used the
// LEGACY places API (google.maps.places.AutocompleteService), which Google has
// been disabling on projects/keys. This helper tries the NEW Places API first
// (AutocompleteSuggestion) and falls back to the legacy one, and it returns a
// human-readable error instead of failing silently, so the UI can show WHY
// suggestions aren't appearing.
//
// The key is read from an env var. The fallback keeps the live site working
// until REACT_APP_GOOGLE_MAPS_KEY is set on Render — once it is (and the key
// is rotated in Google Cloud Console), remove the fallback.
const GOOGLE_MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  'AIzaSyCGP-6vVeGYzy8kRGWtk272i0Qkb1KewXM';

let loadPromise = null;
let authFailed = false;

// Google calls window.gm_authFailure on key/billing/referrer auth errors.
// Capture it so we can tell the user instead of failing silently.
if (typeof window !== 'undefined') {
  const prev = window.gm_authFailure;
  window.gm_authFailure = () => {
    authFailed = true;
    console.error(
      '🚨 Google Maps auth failure — the API key was rejected. Check in Google Cloud Console: ' +
      'key valid & unrestricted for this domain, billing active, Maps JavaScript API enabled.'
    );
    if (typeof prev === 'function') prev();
  };
}

/** Inject the Maps JS script once; resolves when window.google.maps is ready. */
export function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps));
      existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => {
      loadPromise = null; // allow retry
      reject(new Error('Google Maps script failed to load (network or key blocked)'));
    };
    document.head.appendChild(script);
  });
  return loadPromise;
}

/**
 * Fetch US address suggestions for a query.
 * Returns { predictions: [{ description, placeId }], error: string|null }.
 * Tries the NEW Places API first, falls back to the legacy AutocompleteService.
 */
export async function getAddressSuggestions(query) {
  try {
    await loadGoogleMaps();
  } catch (e) {
    return { predictions: [], error: e.message };
  }
  if (authFailed) {
    return {
      predictions: [],
      error: 'Google rejected the API key (auth failure). Check key restrictions, billing, and enabled APIs in Google Cloud Console.',
    };
  }

  let newApiError = null;

  // --- New Places API (Places API "New" must be enabled on the project) ---
  try {
    const places = await window.google.maps.importLibrary('places');
    if (places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        includedRegionCodes: ['us'],
      });
      return {
        predictions: (suggestions || [])
          .filter((s) => s.placePrediction)
          .map((s) => ({
            description: s.placePrediction.text?.text ?? s.placePrediction.text?.toString() ?? '',
            placeId: s.placePrediction.placeId,
          })),
        error: null,
      };
    }
    newApiError = 'AutocompleteSuggestion not available in this Maps JS version';
  } catch (e) {
    newApiError = e.message || String(e);
    console.warn('New Places API failed, falling back to legacy:', newApiError);
  }

  // --- Legacy fallback (requires the legacy "Places API" to be enabled) ---
  try {
    const service = new window.google.maps.places.AutocompleteService();
    return await new Promise((resolve) => {
      service.getPlacePredictions(
        { input: query, componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          const S = window.google.maps.places.PlacesServiceStatus;
          if (status === S.OK && predictions) {
            resolve({
              predictions: predictions.map((p) => ({ description: p.description, placeId: p.place_id })),
              error: null,
            });
          } else if (status === S.ZERO_RESULTS) {
            resolve({ predictions: [], error: null });
          } else {
            console.error('🚨 Google Places error — new API:', newApiError, '| legacy status:', status);
            resolve({
              predictions: [],
              error: `Google Places error: ${status}. (New API also failed: ${newApiError}.) ` +
                'Likely fix: enable "Places API (New)" — and/or the legacy "Places API" — and check billing in Google Cloud Console.',
            });
          }
        }
      );
    });
  } catch (e) {
    console.error('🚨 Both Places APIs failed. New:', newApiError, '| legacy threw:', e);
    return {
      predictions: [],
      error: `Address lookup unavailable: ${newApiError || e.message}`,
    };
  }
}

/**
 * Resolve a placeId to parsed address parts.
 * Returns { street, city, state, zip } (best-effort; missing parts are '').
 */
export async function geocodePlaceId(placeId) {
  await loadGoogleMaps();
  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const parts = { streetNum: '', route: '', city: '', state: 'CA', zip: '' };
        results[0].address_components.forEach((comp) => {
          const t = comp.types;
          if (t.includes('street_number')) parts.streetNum = comp.long_name;
          if (t.includes('route')) parts.route = comp.long_name;
          if (t.includes('locality') || t.includes('sublocality_level_1')) parts.city = comp.long_name;
          if (t.includes('administrative_area_level_1')) parts.state = comp.short_name;
          if (t.includes('postal_code')) parts.zip = comp.long_name;
        });
        resolve({
          street: `${parts.streetNum} ${parts.route}`.trim(),
          city: parts.city,
          state: parts.state,
          zip: parts.zip,
        });
      } else {
        console.error('🚨 Geocoder error:', status);
        reject(new Error(`Geocoder error: ${status}`));
      }
    });
  });
}
